/**
 * REDEX Iron Dome Security Dashboard
 * 
 * Comprehensive security monitoring and response interface
 * Features:
 * - Real-time threat monitoring
 * - IP intelligence and reputation
 * - Fail2ban management
 * - Firewall controls
 * - Request analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  LinearProgress,
  Badge,
  Switch,
  FormControlLabel,
  Slider,
} from '@mui/material';
import {
  Shield,
  Warning,
  Block,
  CheckCircle,
  Refresh,
  Search,
  Visibility,
  Delete,
  Lock,
  LockOpen,
  Security,
  NetworkCheck,
  Storage,
  Speed,
  TrendingUp,
  TrendingDown,
  Public,
  Computer,
  Gavel,
  Radar,
  Language,
  Api,
  BugReport,
  Settings,
  PlayArrow,
  Assessment,
  Timer,
  PersonOff,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import IronDomeGeoMap from '../components/security/IronDomeGeoMap';

// Types
interface ThreatEvent {
  id: number;
  timestamp: string;
  ip_address: string;
  request_method: string;
  request_path: string;
  user_agent: string;
  threat_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: Record<string, any>;
  blocked: boolean;
  country_code?: string;
}

interface SecurityMetrics {
  total_requests_24h: number;
  blocked_requests_24h: number;
  unique_ips_24h: number;
  threats_detected_24h: number;
  top_threat_types: { type: string; count: number }[];
  top_attacking_ips: { ip: string; count: number; country?: string }[];
  top_targeted_paths: { path: string; count: number }[];
  requests_by_country: { country: string; count: number }[];
  hourly_traffic: { hour: number; count: number }[];
  active_blocks: number;
  fail2ban_status: {
    running: boolean;
    jails: {
      name: string;
      currently_failed: number;
      total_failed: number;
      currently_banned: number;
      total_banned: number;
      banned_ips: string[];
    }[];
  };
  // Domain-level metrics
  requests_by_domain?: { domain: string; count: number; api_requests: number }[];
  api_endpoints_by_domain?: { domain: string; endpoint: string; count: number; avg_response_time: number }[];
}

interface SystemStatus {
  nginx_status: boolean;
  fail2ban_status: boolean;
  backend_status: boolean;
  backend_process_count: number;
  honeypot_status: boolean;
  honeypot_service_status?: boolean;
  honeypot_http_status?: boolean;
  iptables_rules: number;
  disk_usage: string;
  memory_usage: string;
  uptime: string;
  last_updated?: string;
}

interface IPIntelligence {
  ip_address: string;
  reputation_score: number;
  threat_count: number;
  first_seen: string;
  last_seen: string;
  is_blocked: boolean;
  country_code?: string;
  city?: string;
  asn?: string;
  isp?: string;
  is_tor: boolean;
  is_vpn: boolean;
  is_proxy: boolean;
  is_datacenter: boolean;
}

// Honeypot types
interface HoneypotStats {
  total_traps_triggered: number;
  traps_24h: number;
  unique_attackers: number;
  total_banned: number;
  active_tarpits: number;
  endpoint_breakdown: Record<string, number>;
  top_attackers: Array<{
    ip: string;
    count: number;
    is_banned: boolean;
    hits_to_ban: number;
  }>;
  recent_traps: Array<{
    timestamp: string;
    ip: string;
    endpoint: string;
    method: string;
    userAgent: string;
  }>;
  attack_patterns: {
    env_access: number;
    php_probes: number;
    symfony_probes: number;
    wordpress_probes: number;
    git_probes: number;
    backup_probes: number;
  };
  hourly_distribution: number[];
}

interface HoneypotConfig {
  tarpitDelay: number;
  autobanThreshold: number;
  tarpitEnabled?: boolean;
  enableTarpit?: boolean;
}

// Severity color mapping
const severityColors: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
};

const API_BASE = '/api/security';

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
});

function formatDateTime(value: string | Date) {
  return dateTimeFormatter.format(new Date(value));
}

function formatThreatLabel(value: string) {
  return value.replace(/_/g, ' ');
}

const IronDomePage: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Data states
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [threats, setThreats] = useState<ThreatEvent[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<{
    database: Array<{
      ip_address: string;
      block_reason: string | null;
      blocked_at: string;
      reputation_score: number;
      threat_count: number;
      country_code: string | null;
    }>;
    fail2ban: Array<{
      jail_name: string;
      banned_ips: string[];
    }>;
  }>({ database: [], fail2ban: [] });
  
  // Honeypot states
  const [honeypotStats, setHoneypotStats] = useState<HoneypotStats | null>(null);
  const [honeypotConfig, setHoneypotConfig] = useState<HoneypotConfig | null>(null);
  const [honeypotOnline, setHoneypotOnline] = useState(false);
  const [honeypotLogs, setHoneypotLogs] = useState<any[]>([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<HoneypotConfig>({
    tarpitDelay: 30000,
    autobanThreshold: 5,
    enableTarpit: true,
  });
  
  // Dialog states
  const [ipDialogOpen, setIpDialogOpen] = useState(false);
  const [selectedIP, setSelectedIP] = useState<string>('');
  const [ipIntel, setIpIntel] = useState<IPIntelligence | null>(null);
  const [ipThreats, setIpThreats] = useState<ThreatEvent[]>([]);
  const [searchIP, setSearchIP] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Daily Check states
  const [dailyCheckDialogOpen, setDailyCheckDialogOpen] = useState(false);
  const [dailyCheckOutput, setDailyCheckOutput] = useState<string>('');
  const [dailyCheckLoading, setDailyCheckLoading] = useState(false);
  const [dailyCheckActionIP, setDailyCheckActionIP] = useState('');
  const [geoBackfillLoading, setGeoBackfillLoading] = useState(false);

  const threatTimeline = React.useMemo(() => {
    const grouped = threats.reduce<Record<string, number>>((accumulator, threat) => {
      const key = new Date(threat.timestamp).toISOString().slice(0, 10);
      accumulator[key] = (accumulator[key] || 0) + 1;
      return accumulator;
    }, {});

    return Object.entries(grouped)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([date, count]) => ({
        date,
        label: dateFormatter.format(new Date(date)),
        threats: count,
      }));
  }, [threats]);

  const threatGeoPoints = React.useMemo(() => {
    const byCountry = threats.reduce<Record<string, number>>((accumulator, threat) => {
      if (!threat.country_code) {
        return accumulator;
      }
      accumulator[threat.country_code] = (accumulator[threat.country_code] || 0) + 1;
      return accumulator;
    }, {});

    return Object.entries(byCountry)
      .map(([countryCode, count]) => ({ countryCode, count }))
      .sort((left, right) => right.count - left.count);
  }, [threats]);

  const knownThreatGeoCount = React.useMemo(
    () => threats.filter((threat) => Boolean(threat.country_code)).length,
    [threats]
  );

  // Run daily check
  const runDailyCheck = async () => {
    setDailyCheckLoading(true);
    setDailyCheckDialogOpen(true);
    setDailyCheckOutput('Running Iron Dome Daily Check...\n');
    
    try {
      const response = await fetch(`${API_BASE}/daily-check`);
      const data = await response.json();
      
      if (data.success) {
        setDailyCheckOutput(data.data.output);
      } else {
        setDailyCheckOutput(`Error: ${data.error}\n${data.details || ''}`);
      }
    } catch (error: any) {
      setDailyCheckOutput(`Failed to run daily check: ${error.message}`);
    } finally {
      setDailyCheckLoading(false);
    }
  };

  // Execute daily check action
  const executeDailyCheckAction = async (action: string, payload: any = {}) => {
    setDailyCheckLoading(true);
    try {
      const response = await fetch(`${API_BASE}/daily-check/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...payload }),
      });
      const data = await response.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `Action '${action}' completed successfully` });
        // Refresh data after action
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Action failed' });
      }
      return data;
    } catch (error: any) {
      setActionMessage({ type: 'error', text: `Action failed: ${error.message}` });
      return { success: false, error: error.message };
    } finally {
      setDailyCheckLoading(false);
    }
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, statusRes, threatsRes, requestsRes, blockedRes] = await Promise.all([
        fetch(`${API_BASE}/metrics`),
        fetch(`${API_BASE}/system-status`),
        fetch(`${API_BASE}/threats?limit=100`),
        fetch(`${API_BASE}/requests?limit=200`),
        fetch(`${API_BASE}/blocked-ips`),
      ]);

      const metricsData = await metricsRes.json();
      const statusData = await statusRes.json();
      const threatsData = await threatsRes.json();
      const requestsData = await requestsRes.json();
      const blockedData = await blockedRes.json();

      if (metricsData.success) setMetrics(metricsData.data);
      if (statusData.success) setSystemStatus(statusData.data);
      if (threatsData.success) setThreats(threatsData.data);
      if (requestsData.success) setRequests(requestsData.data);
      if (blockedData.success) setBlockedIPs(blockedData.data);
      
      // Fetch honeypot data
      try {
        const [honeypotHealthRes, honeypotStatsRes, honeypotLogsRes] = await Promise.all([
          fetch(`${API_BASE}/honeypot/health`),
          fetch(`${API_BASE}/honeypot/stats`),
          fetch(`${API_BASE}/honeypot/logs?limit=50`),
        ]);
        
        const healthData = await honeypotHealthRes.json();
        const statsData = await honeypotStatsRes.json();
        const logsData = await honeypotLogsRes.json();
        
        setHoneypotOnline(healthData.success && healthData.data?.status === 'active');
        
        if (statsData.success) {
          setHoneypotStats(statsData.data);
          setHoneypotConfig(statsData.config);
          setPendingConfig(statsData.config);
        }
        
        if (logsData.success) {
          setHoneypotLogs(logsData.data || []);
        }
      } catch (honeypotError) {
        console.warn('Honeypot service unavailable:', honeypotError);
        setHoneypotOnline(false);
      }
      
      setLoading(false);
      setError(null);
    } catch (err) {
      setError('Failed to fetch security data');
      setLoading(false);
    }
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    fetchData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchData, 30000); // Every 30 seconds (was 10)
      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh]);

  // IP lookup
  const lookupIP = async (ip: string) => {
    setSelectedIP(ip);
    setIpDialogOpen(true);
    setIpIntel(null);
    setIpThreats([]);

    try {
      const res = await fetch(`${API_BASE}/ip/${ip}`);
      const data = await res.json();
      
      if (data.success) {
        setIpIntel(data.data.intel);
        setIpThreats(data.data.threats);
      }
    } catch (err) {
      console.error('Failed to lookup IP', err);
    }
  };

  // Ban IP
  const banIP = async (ip: string, jail: string = 'nginx-env-scanner') => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ip/${ip}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jail }),
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `IP ${ip} has been banned` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to ban IP' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to ban IP' });
    }
    setActionLoading(false);
  };

  // Unban IP
  const unbanIP = async (ip: string, jail: string = 'nginx-env-scanner') => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ip/${ip}/unban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jail }),
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `IP ${ip} has been unbanned` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to unban IP' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to unban IP' });
    }
    setActionLoading(false);
  };

  // Add to firewall
  const addToFirewall = async (ip: string) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ip/${ip}/firewall`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'DROP' }),
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `IP ${ip} added to firewall` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to add to firewall' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to add to firewall' });
    }
    setActionLoading(false);
  };

  // Quick block IP
  const quickBlockIP = async (ip: string, reason: string = 'Blocked from Iron Dome dashboard') => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/quick-block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, reason }),
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `IP ${ip} has been blocked` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to block IP' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to block IP' });
    }
    setActionLoading(false);
  };

  // Honeypot: Ban IP via honeypot
  const honeypotBanIP = async (ip: string) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/honeypot/ban/${ip}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `🐻 IP ${ip} trapped and banned via honeypot` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to ban via honeypot' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to ban via honeypot' });
    }
    setActionLoading(false);
  };

  // Honeypot: Unban IP via honeypot
  const honeypotUnbanIP = async (ip: string) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/honeypot/ban/${ip}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: `IP ${ip} released from honeypot` });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to unban via honeypot' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to unban via honeypot' });
    }
    setActionLoading(false);
  };

  // Honeypot: Update configuration
  const updateHoneypotConfig = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/honeypot/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingConfig),
      });
      const data = await res.json();
      
      if (data.success) {
        setActionMessage({ type: 'success', text: '🛡️ Honeypot configuration updated' });
        setHoneypotConfig(data.config);
        setConfigDialogOpen(false);
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to update config' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Failed to update honeypot config' });
    }
    setActionLoading(false);
  };

  const backfillGeoData = async () => {
    setGeoBackfillLoading(true);
    try {
      const response = await fetch(`${API_BASE}/geolocation/backfill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 75 }),
      });
      const data = await response.json();

      if (data.success) {
        setActionMessage({
          type: 'success',
          text: `Geo backfill complete: ${data.data.enriched} of ${data.data.scanned} IPs enriched`,
        });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Geo backfill failed' });
      }
    } catch {
      setActionMessage({ type: 'error', text: 'Geo backfill failed' });
    } finally {
      setGeoBackfillLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>Loading Iron Dome...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Shield sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Iron Dome Security Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time threat monitoring and response
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated {systemStatus?.last_updated ? formatDateTime(systemStatus.last_updated) : '—'}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
              />
            }
            label="Auto-refresh"
          />
          <Button
            variant="outlined"
            color="info"
            startIcon={<Public />}
            onClick={backfillGeoData}
            disabled={geoBackfillLoading}
          >
            {geoBackfillLoading ? 'Resolving Geo...' : 'Backfill Geo'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Assessment />}
            onClick={runDailyCheck}
          >
            Daily Check
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Action Message */}
      {actionMessage && (
        <Alert 
          severity={actionMessage.type} 
          onClose={() => setActionMessage(null)}
          sx={{ mb: 2 }}
        >
          {actionMessage.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* System Status Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge 
                badgeContent={systemStatus?.nginx_status ? '✓' : '✗'} 
                color={systemStatus?.nginx_status ? 'success' : 'error'}
              >
                <NetworkCheck sx={{ fontSize: 40, color: systemStatus?.nginx_status ? 'success.main' : 'error.main' }} />
              </Badge>
              <Typography variant="h6">Nginx</Typography>
              <Typography variant="body2" color={systemStatus?.nginx_status ? 'success.main' : 'error.main'}>
                {systemStatus?.nginx_status ? 'Active' : 'Inactive'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge 
                badgeContent={systemStatus?.fail2ban_status ? '✓' : '✗'} 
                color={systemStatus?.fail2ban_status ? 'success' : 'error'}
              >
                <Gavel sx={{ fontSize: 40, color: systemStatus?.fail2ban_status ? 'success.main' : 'error.main' }} />
              </Badge>
              <Typography variant="h6">Fail2Ban</Typography>
              <Typography variant="body2" color={systemStatus?.fail2ban_status ? 'success.main' : 'error.main'}>
                {systemStatus?.fail2ban_status ? 'Active' : 'Inactive'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge
                badgeContent={systemStatus?.backend_status ? '✓' : '✗'}
                color={systemStatus?.backend_status ? 'success' : 'error'}
              >
                <Api sx={{ fontSize: 40, color: systemStatus?.backend_status ? 'success.main' : 'error.main' }} />
              </Badge>
              <Typography variant="h6">Backend</Typography>
              <Typography variant="body2" color={systemStatus?.backend_status ? 'success.main' : 'error.main'}>
                {systemStatus?.backend_status ? `PM2 Online (${systemStatus?.backend_process_count || 0})` : 'PM2 Offline'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge
                badgeContent={systemStatus?.honeypot_status ? '✓' : '✗'}
                color={systemStatus?.honeypot_status ? 'success' : 'warning'}
              >
                <BugReport sx={{ fontSize: 40, color: systemStatus?.honeypot_status ? 'warning.main' : 'text.disabled' }} />
              </Badge>
              <Typography variant="h6">Honeypot</Typography>
              <Typography variant="body2" color={systemStatus?.honeypot_status ? 'warning.main' : 'text.secondary'}>
                {systemStatus?.honeypot_service_status
                  ? systemStatus?.honeypot_http_status
                    ? 'Systemd + HTTP Active'
                    : 'Systemd Active, HTTP Check Failing'
                  : 'Systemd Inactive'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Lock sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h6">Firewall Rules</Typography>
              <Typography variant="h4" color="primary.main">
                {systemStatus?.iptables_rules || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Storage sx={{ fontSize: 40, color: 'info.main' }} />
              <Typography variant="h6">Disk Usage</Typography>
              <Typography variant="h5" color="info.main">
                {systemStatus?.disk_usage || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Speed sx={{ fontSize: 40, color: 'warning.main' }} />
              <Typography variant="h6">Memory</Typography>
              <Typography variant="h5" color="warning.main">
                {systemStatus?.memory_usage || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Computer sx={{ fontSize: 40, color: 'success.main' }} />
              <Typography variant="h6">Uptime</Typography>
              <Typography variant="body2" color="success.main">
                {systemStatus?.uptime?.replace('up ', '') || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Metrics Overview */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Requests (24h)</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics?.total_requests_24h?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 50, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Threats Detected</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics?.threats_detected_24h?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 50, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Unique IPs</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics?.unique_ips_24h?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Public sx={{ fontSize: 50, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Active Blocks</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics?.active_blocks?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Block sx={{ fontSize: 50, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* IP Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              label="Search IP Address"
              placeholder="Enter IP address to investigate..."
              value={searchIP}
              onChange={(e) => setSearchIP(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchIP && lookupIP(searchIP)}
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={() => searchIP && lookupIP(searchIP)}
              disabled={!searchIP}
            >
              Investigate
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<Warning />} label="Threats" />
        <Tab icon={<Radar />} label="Live Requests" />
        <Tab icon={<Block />} label="Blocked IPs" />
        <Tab icon={<Gavel />} label="Fail2Ban Jails" />
        <Tab icon={<BugReport />} label="🐻 Honeypot" />
        <Tab icon={<Language />} label="Domain Analytics" />
        <Tab icon={<TrendingUp />} label="Analytics" />
      </Tabs>

      {/* Tab Panels */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Threat Events
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Showing full date and time for the latest 50 threat events.
              </Typography>
              <Chip
                label={`Geo resolved ${knownThreatGeoCount}/${threats.length}`}
                color={knownThreatGeoCount > 0 ? 'info' : 'default'}
                variant="outlined"
              />
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Detected At</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Threat Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {threats.slice(0, 50).map((threat) => (
                    <TableRow key={threat.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(threat.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Click to investigate">
                          <Button
                            size="small"
                            onClick={() => lookupIP(threat.ip_address)}
                            sx={{ textTransform: 'none' }}
                          >
                            {threat.ip_address}
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{threat.country_code || '—'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={formatThreatLabel(threat.threat_type)} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={threat.severity} 
                          size="small"
                          color={severityColors[threat.severity]}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {threat.request_path}
                      </TableCell>
                      <TableCell>
                        {threat.blocked ? (
                          <Chip label="Blocked" size="small" color="error" />
                        ) : (
                          <Chip label="Logged" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ban IP">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => banIP(threat.ip_address)}
                          >
                            <Block />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add to Firewall">
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => addToFirewall(threat.ip_address)}
                          >
                            <Lock />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Live Request Feed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing last {requests.length} requests
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Captured At</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Reputation</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.slice(0, 200).map((req, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(req.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => lookupIP(req.ip_address)}
                          sx={{ textTransform: 'none' }}
                        >
                          {req.ip_address}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={req.request_method} 
                          size="small"
                          color={req.request_method === 'GET' ? 'info' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={req.request_path}>
                          <span>{req.request_path}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={req.status_code} 
                          size="small"
                          color={req.status_code < 400 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{req.response_time_ms}ms</TableCell>
                      <TableCell>
                        {req.reputation_score !== undefined ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress 
                              variant="determinate" 
                              value={req.reputation_score} 
                              sx={{ width: 60, height: 8, borderRadius: 4 }}
                              color={req.reputation_score > 50 ? 'success' : req.reputation_score > 20 ? 'warning' : 'error'}
                            />
                            <Typography variant="caption">{req.reputation_score}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Block IP">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => quickBlockIP(req.ip_address)}
                          >
                            <Block />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Investigate">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => lookupIP(req.ip_address)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Blocked IP Addresses
              </Typography>
              <Chip 
                label={`${blockedIPs.database.length + blockedIPs.fail2ban.reduce((acc, j) => acc + j.banned_ips.length, 0)} Total Blocked`}
                color="error"
              />
            </Box>
            
            {/* Database Blocked IPs */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
              Iron Dome Database Blocks
            </Typography>
            {blockedIPs.database.length > 0 ? (
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Blocked At</TableCell>
                      <TableCell>Threat Count</TableCell>
                      <TableCell>Reputation</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blockedIPs.database.map((ip) => (
                      <TableRow key={ip.ip_address} hover>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => lookupIP(ip.ip_address)}
                            sx={{ textTransform: 'none' }}
                            color="error"
                          >
                            {ip.ip_address}
                          </Button>
                        </TableCell>
                        <TableCell>{ip.country_code || '—'}</TableCell>
                        <TableCell>{ip.block_reason || 'Manual block'}</TableCell>
                        <TableCell>{formatDateTime(ip.blocked_at)}</TableCell>
                        <TableCell>
                          <Chip label={ip.threat_count} size="small" color={ip.threat_count > 5 ? 'error' : 'warning'} />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress 
                              variant="determinate" 
                              value={ip.reputation_score} 
                              sx={{ width: 60, height: 8, borderRadius: 4 }}
                              color="error"
                            />
                            <Typography variant="caption">{ip.reputation_score}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Unblock IP">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => unbanIP(ip.ip_address)}
                            >
                              <LockOpen />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Investigate">
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => lookupIP(ip.ip_address)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>No IPs currently blocked in database</Alert>
            )}
            
            {/* Fail2Ban Blocked IPs */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
              Fail2Ban Blocks
            </Typography>
            {blockedIPs.fail2ban.length > 0 ? (
              <Grid container spacing={2}>
                {blockedIPs.fail2ban.map((jail) => (
                  <Grid item xs={12} md={6} key={jail.jail_name}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">{jail.jail_name}</Typography>
                          <Chip label={`${jail.banned_ips.length} blocked`} size="small" color="error" />
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {jail.banned_ips.map((ip) => (
                            <Chip
                              key={ip}
                              label={ip}
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => lookupIP(ip)}
                              onDelete={() => unbanIP(ip, jail.jail_name)}
                              deleteIcon={<LockOpen />}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">No IPs currently blocked in Fail2Ban</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <Grid container spacing={2}>
          {metrics?.fail2ban_status?.jails?.map((jail) => (
            <Grid item xs={12} md={6} key={jail.name}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{jail.name}</Typography>
                    <Chip 
                      label={`${jail.currently_banned} banned`} 
                      color={jail.currently_banned > 0 ? 'error' : 'success'}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Currently Failed</Typography>
                      <Typography variant="h5">{jail.currently_failed}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Failed</Typography>
                      <Typography variant="h5">{jail.total_failed}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Currently Banned</Typography>
                      <Typography variant="h5" color="error.main">{jail.currently_banned}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Banned</Typography>
                      <Typography variant="h5">{jail.total_banned}</Typography>
                    </Grid>
                  </Grid>
                  {jail.banned_ips.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Currently Banned IPs:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {jail.banned_ips.map((ip) => (
                          <Chip
                            key={ip}
                            label={ip}
                            size="small"
                            color="error"
                            onDelete={() => unbanIP(ip, jail.name)}
                            deleteIcon={<LockOpen />}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 🐻 HONEYPOT TAB */}
      {activeTab === 4 && (
        <Box>
          {/* Honeypot Status Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Badge 
                badgeContent={honeypotOnline ? '✓' : '✗'} 
                color={honeypotOnline ? 'success' : 'error'}
              >
                <BugReport sx={{ fontSize: 40, color: honeypotOnline ? 'warning.main' : 'error.main' }} />
              </Badge>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  🐻 Bear Trap Honeypot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {honeypotOnline ? 'Active - Trapping intruders' : 'Offline - Service unavailable'}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setConfigDialogOpen(true)}
                disabled={!honeypotOnline}
              >
                Configure
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchData}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {!honeypotOnline ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Honeypot Service Offline</Typography>
              <Typography variant="body2">
                The honeypot HTTP service is not responding on the configured Iron Dome endpoint. Verify the service bound to port 6680 and the configured admin key.
              </Typography>
            </Alert>
          ) : (
            <>
              {/* Honeypot Stats Cards */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Traps</Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {honeypotStats?.total_traps_triggered || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Last 24h</Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {honeypotStats?.traps_24h || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Unique Attackers</Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {honeypotStats?.unique_attackers || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Auto-Banned</Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {honeypotStats?.total_banned || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Tarpit Delay</Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {((honeypotConfig?.tarpitDelay || 30000) / 1000)}s
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Card sx={{ background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)', color: 'white' }}>
                    <CardContent>
                      <Box textAlign="center">
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Ban Threshold</Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {honeypotConfig?.autobanThreshold || 5} hits
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Attack Patterns Breakdown */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        🎯 Attack Pattern Distribution
                      </Typography>
                      {honeypotStats?.attack_patterns && (
                        <Box>
                          {[
                            { key: 'env_access', label: '.env File Probes', icon: '📄', color: 'error' },
                            { key: 'php_probes', label: 'PHP Info Probes', icon: '🐘', color: 'warning' },
                            { key: 'symfony_probes', label: 'Symfony/Profiler', icon: '🔧', color: 'info' },
                            { key: 'wordpress_probes', label: 'WordPress Probes', icon: '📝', color: 'primary' },
                            { key: 'git_probes', label: 'Git Exposure', icon: '🔓', color: 'secondary' },
                            { key: 'backup_probes', label: 'Backup Files', icon: '💾', color: 'success' },
                          ].map(({ key, label, icon, color }) => {
                            const count = honeypotStats.attack_patterns[key as keyof typeof honeypotStats.attack_patterns] || 0;
                            const maxCount = Math.max(...Object.values(honeypotStats.attack_patterns || {}), 1);
                            return (
                              <Box key={key} display="flex" alignItems="center" gap={2} mb={1.5}>
                                <Typography sx={{ width: 30 }}>{icon}</Typography>
                                <Box flexGrow={1}>
                                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="body2">{label}</Typography>
                                    <Typography variant="body2" fontWeight="bold">{count}</Typography>
                                  </Box>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={(count / maxCount) * 100}
                                    color={color as any}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        🕵️ Top Intruders
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>IP Address</TableCell>
                              <TableCell align="center">Hits</TableCell>
                              <TableCell align="center">Status</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {honeypotStats?.top_attackers?.slice(0, 8).map((attacker) => (
                              <TableRow key={attacker.ip} hover>
                                <TableCell>
                                  <Button
                                    size="small"
                                    onClick={() => lookupIP(attacker.ip)}
                                    sx={{ textTransform: 'none' }}
                                    color={attacker.is_banned ? 'error' : 'primary'}
                                  >
                                    {attacker.ip}
                                  </Button>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={attacker.count} 
                                    size="small" 
                                    color={attacker.count >= (honeypotConfig?.autobanThreshold || 5) ? 'error' : 'warning'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {attacker.is_banned ? (
                                    <Chip label="BANNED" size="small" color="error" icon={<Block />} />
                                  ) : (
                                    <Chip 
                                      label={`${attacker.hits_to_ban} to ban`} 
                                      size="small" 
                                      color="warning" 
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  {attacker.is_banned ? (
                                    <Tooltip title="Unban from Honeypot">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        onClick={() => honeypotUnbanIP(attacker.ip)}
                                      >
                                        <LockOpen />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Ban via Honeypot">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => honeypotBanIP(attacker.ip)}
                                      >
                                        <PersonOff />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Recent Honeypot Traps */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🪤 Recent Trap Events
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>IP Address</TableCell>
                          <TableCell>Trap Endpoint</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>User Agent</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {honeypotLogs?.slice(0, 15).map((log, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontSize={11}>
                                {formatDateTime(log.timestamp)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => lookupIP(log.ip)}
                                sx={{ textTransform: 'none' }}
                              >
                                {log.ip}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={log.endpoint} 
                                size="small" 
                                color={
                                  log.endpoint?.includes('.env') ? 'error' :
                                  log.endpoint?.includes('php') ? 'warning' :
                                  log.endpoint?.includes('wp-') ? 'info' :
                                  'default'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={log.method} 
                                size="small"
                                color={log.method === 'POST' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <Tooltip title={log.userAgent}>
                                <Typography variant="caption" noWrap>
                                  {log.userAgent?.substring(0, 40)}...
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Block IP">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => honeypotBanIP(log.ip)}
                                >
                                  <Block />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Add to Firewall">
                                <IconButton 
                                  size="small" 
                                  color="warning"
                                  onClick={() => addToFirewall(log.ip)}
                                >
                                  <Lock />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      )}

      {activeTab === 5 && (
        <Grid container spacing={2}>
          {/* Domain Traffic Overview */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Language color="primary" />
                  <Typography variant="h6">Domain Traffic (24h)</Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Domain</TableCell>
                        <TableCell align="right">Total Requests</TableCell>
                        <TableCell align="right">API Requests</TableCell>
                        <TableCell align="right">API %</TableCell>
                        <TableCell>Traffic Distribution</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.requests_by_domain?.length ? (
                        metrics.requests_by_domain.map((d) => {
                          const maxCount = metrics.requests_by_domain?.[0]?.count || 1;
                          const apiPercent = d.count > 0 ? Math.round((d.api_requests / d.count) * 100) : 0;
                          return (
                            <TableRow key={d.domain} hover>
                              <TableCell>
                                <Chip 
                                  icon={<Public />} 
                                  label={d.domain} 
                                  size="small" 
                                  variant="outlined"
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">{d.count.toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="primary">{d.api_requests.toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${apiPercent}%`} 
                                  size="small" 
                                  color={apiPercent > 80 ? 'success' : apiPercent > 50 ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 200 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={(d.count / maxCount) * 100}
                                  sx={{ height: 10, borderRadius: 5 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary" py={2}>
                              No domain data yet. Requests will appear as traffic flows through the system.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* API Endpoints by Domain */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Api color="secondary" />
                  <Typography variant="h6">API Endpoints by Domain (24h)</Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Domain</TableCell>
                        <TableCell>API Endpoint</TableCell>
                        <TableCell align="right">Requests</TableCell>
                        <TableCell align="right">Avg Response Time</TableCell>
                        <TableCell>Performance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.api_endpoints_by_domain?.length ? (
                        metrics.api_endpoints_by_domain.map((e, idx) => {
                          const performanceColor = e.avg_response_time < 100 ? 'success' : 
                                                   e.avg_response_time < 500 ? 'warning' : 'error';
                          return (
                            <TableRow key={`${e.domain}-${e.endpoint}-${idx}`} hover>
                              <TableCell>
                                <Chip 
                                  label={e.domain} 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                                  {e.endpoint}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">{e.count.toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  icon={<Speed />}
                                  label={`${Math.round(e.avg_response_time)}ms`} 
                                  size="small" 
                                  color={performanceColor}
                                />
                              </TableCell>
                              <TableCell sx={{ width: 120 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={Math.min(100, (e.avg_response_time / 1000) * 100)}
                                  color={performanceColor}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary" py={2}>
                              No API data yet. API requests will appear as they are logged.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 6 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6">Threat Timeline</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recent Iron Dome threat activity grouped by attack date.
                    </Typography>
                  </Box>
                  <Chip label={`${threatTimeline.length} date buckets`} variant="outlined" />
                </Box>
                <Box sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <AreaChart data={threatTimeline}>
                      <defs>
                        <linearGradient id="ironDomeThreatGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.65} />
                          <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0.06} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="label" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="threats" stroke={theme.palette.error.main} fill="url(#ironDomeThreatGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <IronDomeGeoMap
              points={threatGeoPoints}
              totalThreats={threats.length}
              knownThreats={knownThreatGeoCount}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Top Threat Types</Typography>
                {metrics?.top_threat_types?.map((t, idx) => (
                  <Box key={t.type} display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography sx={{ width: 30 }}>{idx + 1}.</Typography>
                    <Box flexGrow={1}>
                      <Typography variant="body2">{formatThreatLabel(t.type)}</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(t.count / (metrics?.top_threat_types?.[0]?.count || 1)) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">{t.count}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Top Attacking IPs</Typography>
                {metrics?.top_attacking_ips?.map((t, idx) => (
                  <Box key={t.ip} display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography sx={{ width: 30 }}>{idx + 1}.</Typography>
                    <Button
                      size="small"
                      onClick={() => lookupIP(t.ip)}
                      sx={{ textTransform: 'none', minWidth: 130 }}
                    >
                      {t.ip}
                    </Button>
                    <Chip label={t.country || '—'} size="small" variant="outlined" />
                    <Box flexGrow={1}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(t.count / (metrics?.top_attacking_ips?.[0]?.count || 1)) * 100}
                        color="error"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">{t.count}</Typography>
                    <IconButton size="small" color="error" onClick={() => banIP(t.ip)}>
                      <Block />
                    </IconButton>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Top Targeted Paths</Typography>
                {metrics?.top_targeted_paths?.map((t, idx) => (
                  <Box key={t.path} display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography sx={{ width: 30 }}>{idx + 1}.</Typography>
                    <Typography variant="body2" sx={{ minWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.path}
                    </Typography>
                    <Box flexGrow={1}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(t.count / (metrics?.top_targeted_paths?.[0]?.count || 1)) * 100}
                        color="warning"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">{t.count}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Requests by Country</Typography>
                {metrics?.requests_by_country?.map((t, idx) => (
                  <Box key={t.country} display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography sx={{ width: 30 }}>{idx + 1}.</Typography>
                    <Typography variant="body2" sx={{ minWidth: 80 }}>{t.country}</Typography>
                    <Box flexGrow={1}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(t.count / (metrics?.requests_by_country?.[0]?.count || 1)) * 100}
                        color="info"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">{t.count}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* IP Intelligence Dialog */}
      <Dialog open={ipDialogOpen} onClose={() => setIpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Security color="primary" />
            IP Intelligence: {selectedIP}
          </Box>
        </DialogTitle>
        <DialogContent>
          {ipIntel ? (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Reputation Score</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress 
                      variant="determinate" 
                      value={ipIntel.reputation_score} 
                      sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                      color={ipIntel.reputation_score > 50 ? 'success' : ipIntel.reputation_score > 20 ? 'warning' : 'error'}
                    />
                    <Typography variant="h6">{ipIntel.reputation_score}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Threat Count</Typography>
                  <Typography variant="h5" color={ipIntel.threat_count > 0 ? 'error.main' : 'success.main'}>
                    {ipIntel.threat_count}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Country</Typography>
                  <Typography variant="h6">{ipIntel.country_code || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={ipIntel.is_blocked ? 'BLOCKED' : 'ACTIVE'} 
                    color={ipIntel.is_blocked ? 'error' : 'success'}
                  />
                </Grid>
              </Grid>

              <Box display="flex" gap={1} mb={3}>
                {ipIntel.is_tor && <Chip label="TOR" color="error" size="small" />}
                {ipIntel.is_vpn && <Chip label="VPN" color="warning" size="small" />}
                {ipIntel.is_proxy && <Chip label="Proxy" color="warning" size="small" />}
                {ipIntel.is_datacenter && <Chip label="Datacenter" color="info" size="small" />}
              </Box>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">First Seen</Typography>
                  <Typography>{formatDateTime(ipIntel.first_seen)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Last Seen</Typography>
                  <Typography>{formatDateTime(ipIntel.last_seen)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">City</Typography>
                  <Typography>{ipIntel.city || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">ISP</Typography>
                  <Typography>{ipIntel.isp || 'Unknown'}</Typography>
                </Grid>
              </Grid>

              {ipThreats.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Recent Threats from this IP</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Path</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ipThreats.slice(0, 10).map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>{formatDateTime(t.timestamp)}</TableCell>
                            <TableCell>{t.threat_type}</TableCell>
                            <TableCell>
                              <Chip label={t.severity} size="small" color={severityColors[t.severity]} />
                            </TableCell>
                            <TableCell>{t.request_path}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIpDialogOpen(false)}>Close</Button>
          {ipIntel && !ipIntel.is_blocked && (
            <>
              <Button 
                color="warning" 
                startIcon={<Block />}
                onClick={() => { banIP(selectedIP); setIpDialogOpen(false); }}
                disabled={actionLoading}
              >
                Ban IP
              </Button>
              <Button 
                color="error" 
                startIcon={<Lock />}
                onClick={() => { addToFirewall(selectedIP); setIpDialogOpen(false); }}
                disabled={actionLoading}
              >
                Add to Firewall
              </Button>
            </>
          )}
          {ipIntel?.is_blocked && (
            <Button 
              color="success" 
              startIcon={<LockOpen />}
              onClick={() => { unbanIP(selectedIP); setIpDialogOpen(false); }}
              disabled={actionLoading}
            >
              Unban IP
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Honeypot Configuration Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Settings color="primary" />
            🐻 Honeypot Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography gutterBottom>
              <Timer sx={{ verticalAlign: 'middle', mr: 1 }} />
              Tarpit Delay: <strong>{(pendingConfig.tarpitDelay / 1000).toFixed(0)}s</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              How long to slow down attacker requests (wastes their time)
            </Typography>
            <Slider
              value={pendingConfig.tarpitDelay}
              onChange={(_, v) => setPendingConfig({ ...pendingConfig, tarpitDelay: v as number })}
              min={5000}
              max={120000}
              step={5000}
              marks={[
                { value: 5000, label: '5s' },
                { value: 30000, label: '30s' },
                { value: 60000, label: '60s' },
                { value: 120000, label: '120s' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v / 1000}s`}
            />

            <Typography gutterBottom sx={{ mt: 3 }}>
              <PersonOff sx={{ verticalAlign: 'middle', mr: 1 }} />
              Auto-Ban Threshold: <strong>{pendingConfig.autobanThreshold} hits</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Number of honeypot hits before automatic IP ban
            </Typography>
            <Slider
              value={pendingConfig.autobanThreshold}
              onChange={(_, v) => setPendingConfig({ ...pendingConfig, autobanThreshold: v as number })}
              min={1}
              max={20}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
              ]}
              valueLabelDisplay="auto"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={pendingConfig.enableTarpit ?? true}
                  onChange={(e) => setPendingConfig({ ...pendingConfig, enableTarpit: e.target.checked })}
                />
              }
              label="Enable Tarpit (slow responses)"
              sx={{ mt: 3 }}
            />

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>How it works:</strong> When attackers probe honeypot endpoints 
                (like /.env or /phpinfo.php), they receive fake "valuable" data while being 
                tarpitted (slowed down). After hitting the threshold, they're auto-banned.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={updateHoneypotConfig}
            disabled={actionLoading}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Check Dialog */}
      <Dialog 
        open={dailyCheckDialogOpen} 
        onClose={() => setDailyCheckDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment color="success" />
          Iron Dome Daily Check
          {dailyCheckLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Paper 
              sx={{ 
                p: 2, 
                bgcolor: '#1a1a1a', 
                color: '#00ff00',
                fontFamily: 'monospace',
                fontSize: '12px',
                maxHeight: '400px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {dailyCheckOutput || 'Click "Run Check" to start...'}
            </Paper>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Quick Actions</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="IP Address"
                  value={dailyCheckActionIP}
                  onChange={(e) => setDailyCheckActionIP(e.target.value)}
                  placeholder="Enter IP to ban or scan"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    if (dailyCheckActionIP) {
                      executeDailyCheckAction('ban', { ip: dailyCheckActionIP });
                    }
                  }}
                  disabled={!dailyCheckActionIP || dailyCheckLoading}
                  startIcon={<Block />}
                >
                  Ban IP
                </Button>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="info"
                  onClick={async () => {
                    if (dailyCheckActionIP) {
                      const result = await executeDailyCheckAction('scan', { ip: dailyCheckActionIP });
                      if (result.success) {
                        setDailyCheckOutput(prev => 
                          prev + `\n\n=== SCAN RESULT for ${dailyCheckActionIP} ===\n` +
                          `Tor: ${result.fingerprint?.is_tor ? 'YES' : 'No'}\n` +
                          `VPN: ${result.vpn_analysis?.vpn_detected ? 'YES' : 'No'}\n` +
                          `Confidence: ${result.vpn_analysis?.confidence || 0}%\n` +
                          `Recommended: ${result.vpn_analysis?.recommended_action || 'monitor'}\n`
                        );
                      }
                    }
                  }}
                  disabled={!dailyCheckActionIP || dailyCheckLoading}
                  startIcon={<Search />}
                >
                  Scan IP
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  onClick={() => executeDailyCheckAction('ban_top_attackers')}
                  disabled={dailyCheckLoading}
                  startIcon={<Gavel />}
                >
                  Ban All Top Attackers
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="warning"
                  onClick={async () => {
                    const result = await executeDailyCheckAction('ban_top_attackers');
                    if (result.success && result.results) {
                      const ips = result.results.filter((r: any) => r.banned).map((r: any) => r.ip);
                      if (ips.length > 0) {
                        await executeDailyCheckAction('parasitic', { ips });
                      }
                    }
                  }}
                  disabled={dailyCheckLoading}
                  startIcon={<BugReport />}
                >
                  Ban + Deploy Parasitic
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyCheckDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={runDailyCheck}
            disabled={dailyCheckLoading}
            startIcon={<PlayArrow />}
          >
            Run Check
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IronDomePage;
